// ── Word state constants ────────────────────────────────
export const WORD_STATE = {
  NEW:        0,
  LEARNING:   1,
  CONFIDENT:  2,
  MASTERED:   3,
};

export function getWordState(p) {
  if (!p || p.attempts === 0) return WORD_STATE.NEW;
  if (p.streak >= 5) return WORD_STATE.MASTERED;
  if (p.streak >= 3) return WORD_STATE.CONFIDENT;
  return WORD_STATE.LEARNING;
}

export function updateProgress(progress, userId, wordId, correct) {
  const key = `${userId}_${wordId}`;
  const prev = progress[key] || { correct: 0, attempts: 0, streak: 0, intervalDays: 1 };
  const streak = correct ? prev.streak + 1 : 0;
  let intervalDays = prev.intervalDays;
  if (correct) {
    if (streak >= 5)      intervalDays = 30;
    else if (streak >= 3) intervalDays = Math.min(prev.intervalDays * 2.5, 14);
    else                  intervalDays = Math.min(prev.intervalDays * 1.5, 3);
  } else {
    intervalDays = 1;
  }
  const state = streak >= 5 ? WORD_STATE.MASTERED : streak >= 3 ? WORD_STATE.CONFIDENT : WORD_STATE.LEARNING;
  return {
    ...progress,
    [key]: {
      correct: prev.correct + (correct ? 1 : 0),
      attempts: prev.attempts + 1,
      streak,
      intervalDays,
      due: Date.now() + intervalDays * 86400000,
      state,
      mastered: streak >= 5,
    }
  };
}

export function buildMasterPool(wordLists) {
  const now = Date.now();
  const seen = new Set();
  const pool = [];
  for (const list of wordLists) {
    if (!list.active) continue;
    if (list.activeFrom && now < new Date(list.activeFrom).getTime()) continue;
    if (list.activeTo   && now > new Date(list.activeTo).getTime() + 86400000) continue;
    for (const w of list.words) {
      const key = w.word.toLowerCase();
      if (!seen.has(key)) { seen.add(key); pool.push(w); }
    }
  }
  return pool;
}

export function selectWords(wordListsOrPool, progress, userId, count) {
  const now = Date.now();
  const allWords = Array.isArray(wordListsOrPool)
    ? buildMasterPool(wordListsOrPool)
    : wordListsOrPool.words || [];

  if (allWords.length === 0) return [];

  const buckets = { new: [], overdue: [], learning: [], confident: [], mastered: [] };

  for (const w of allWords) {
    const p = progress[`${userId}_${w.id}`];
    const state = getWordState(p);
    if (state === WORD_STATE.NEW) {
      buckets.new.push(w);
    } else if (state === WORD_STATE.MASTERED) {
      if (Math.random() < 0.033) buckets.mastered.push(w);
    } else if (state === WORD_STATE.CONFIDENT) {
      if (p.due <= now) buckets.confident.push(w);
      else if (Math.random() < 0.1) buckets.confident.push(w);
    } else {
      if (p.due <= now) buckets.overdue.push(w);
      else buckets.learning.push(w);
    }
  }

  const shuffle = arr => arr.sort(() => Math.random() - 0.5);
  shuffle(buckets.new);
  shuffle(buckets.overdue);
  shuffle(buckets.learning);
  shuffle(buckets.confident);
  shuffle(buckets.mastered);

  const session = [];
  const take = (arr) => { while (session.length < count && arr.length) session.push(arr.shift()); };
  take(buckets.overdue);
  take(buckets.new);
  take(buckets.learning);
  take(buckets.confident);
  take(buckets.mastered);

  if (session.length < count) {
    const inSession = new Set(session.map(w => w.id));
    const remaining = allWords.filter(w => !inSession.has(w.id));
    shuffle(remaining);
    take(remaining);
  }

  return session.slice(0, count);
}

// ── Meaning progress ─────────────────────────────────────
export function getMeaningKey(userId, wordId) { return `${userId}_meaning_${wordId}`; }
export function updateMeaningProgress(mProgress, userId, wordId, correct) {
  const key = getMeaningKey(userId, wordId);
  const prev = mProgress[key] || { correct: 0, attempts: 0, streak: 0, intervalDays: 1 };
  const streak = correct ? prev.streak + 1 : 0;
  let intervalDays = prev.intervalDays;
  if (correct) {
    if (streak >= 5)      intervalDays = 30;
    else if (streak >= 3) intervalDays = Math.min(prev.intervalDays * 2.5, 14);
    else                  intervalDays = Math.min(prev.intervalDays * 1.5, 3);
  } else { intervalDays = 1; }
  const state = streak >= 5 ? WORD_STATE.MASTERED : streak >= 3 ? WORD_STATE.CONFIDENT : WORD_STATE.LEARNING;
  return { ...mProgress, [key]: { correct: prev.correct + (correct ? 1 : 0), attempts: prev.attempts + 1, streak, intervalDays, due: Date.now() + intervalDays * 86400000, state, mastered: streak >= 5 } };
}

export function selectMeaningWords(wordLists, mProgress, userId, count) {
  const pool = buildMasterPool(wordLists);
  const now = Date.now();
  const buckets = { new: [], overdue: [], learning: [], confident: [], mastered: [] };
  for (const w of pool) {
    const p = mProgress[getMeaningKey(userId, w.id)];
    const state = getWordState(p);
    if (state === WORD_STATE.NEW) buckets.new.push(w);
    else if (state === WORD_STATE.MASTERED) { if (Math.random() < 0.033) buckets.mastered.push(w); }
    else if (state === WORD_STATE.CONFIDENT) { if (p.due <= now) buckets.confident.push(w); else if (Math.random() < 0.1) buckets.confident.push(w); }
    else { if (p.due <= now) buckets.overdue.push(w); else buckets.learning.push(w); }
  }
  const shuffle = arr => arr.sort(() => Math.random() - 0.5);
  Object.values(buckets).forEach(shuffle);
  const session = [];
  const take = arr => { while (session.length < count && arr.length) session.push(arr.shift()); };
  take(buckets.overdue); take(buckets.new); take(buckets.learning); take(buckets.confident); take(buckets.mastered);
  if (session.length < count) {
    const inSession = new Set(session.map(w => w.id));
    const remaining = pool.filter(w => !inSession.has(w.id));
    shuffle(remaining); take(remaining);
  }
  return session.slice(0, count);
}

// ── Synonym/Antonym progress ──────────────────────────────
export function getSynKey(userId, wordId) { return `${userId}_syn_${wordId}`; }
export function getAntKey(userId, wordId) { return `${userId}_ant_${wordId}`; }

export function updateSynProgress(prog, userId, wordId, correct) {
  const key = getSynKey(userId, wordId);
  const prev = prog[key] || { correct: 0, attempts: 0, streak: 0, intervalDays: 1 };
  const streak = correct ? prev.streak + 1 : 0;
  let intervalDays = prev.intervalDays;
  if (correct) {
    if (streak >= 5)      intervalDays = 30;
    else if (streak >= 3) intervalDays = Math.min(prev.intervalDays * 2.5, 14);
    else                  intervalDays = Math.min(prev.intervalDays * 1.5, 3);
  } else { intervalDays = 1; }
  const state = streak >= 5 ? WORD_STATE.MASTERED : streak >= 3 ? WORD_STATE.CONFIDENT : WORD_STATE.LEARNING;
  return { ...prog, [key]: { correct: prev.correct + (correct ? 1 : 0), attempts: prev.attempts + 1, streak, intervalDays, due: Date.now() + intervalDays * 86400000, state, mastered: streak >= 5 } };
}

export function updateAntProgress(prog, userId, wordId, correct) {
  const key = getAntKey(userId, wordId);
  const prev = prog[key] || { correct: 0, attempts: 0, streak: 0, intervalDays: 1 };
  const streak = correct ? prev.streak + 1 : 0;
  let intervalDays = prev.intervalDays;
  if (correct) {
    if (streak >= 5)      intervalDays = 30;
    else if (streak >= 3) intervalDays = Math.min(prev.intervalDays * 2.5, 14);
    else                  intervalDays = Math.min(prev.intervalDays * 1.5, 3);
  } else { intervalDays = 1; }
  const state = streak >= 5 ? WORD_STATE.MASTERED : streak >= 3 ? WORD_STATE.CONFIDENT : WORD_STATE.LEARNING;
  return { ...prog, [key]: { correct: prev.correct + (correct ? 1 : 0), attempts: prev.attempts + 1, streak, intervalDays, due: Date.now() + intervalDays * 86400000, state, mastered: streak >= 5 } };
}

// ── TTS ───────────────────────────────────────────────────
export const ACCENT_LOCALES = [
  { code: "en-AU", label: "🇦🇺 Australian" },
  { code: "en-GB", label: "🇬🇧 British" },
  { code: "en-US", label: "🇺🇸 American" },
  { code: "en-IE", label: "🇮🇪 Irish" },
  { code: "en-IN", label: "🇮🇳 Indian" },
  { code: "en-NZ", label: "🇳🇿 New Zealand" },
];

export function getAvailableVoices() {
  return window.speechSynthesis?.getVoices() || [];
}

export function findVoice(voicePrefs) {
  const voices = getAvailableVoices();
  if (!voices.length) return null;
  const locale = voicePrefs?.accent || "en-AU";
  const gender = voicePrefs?.gender || "any";

  let pool = voices.filter(v => v.lang.startsWith(locale.split("-")[0]) && v.lang.includes(locale.split("-")[1] || ""));
  if (!pool.length) pool = voices.filter(v => v.lang.startsWith("en"));

  if (gender !== "any") {
    const femaleKeywords = ["female","woman","girl","samantha","victoria","karen","moira","fiona","veena","zira","catherine","susan","allison","ava","kate","serena","tessa"];
    const maleKeywords   = ["male","man","boy","daniel","thomas","alex","fred","bruce","lee","rishi","liam","oliver","james","aaron","gordon","jorge"];
    const keywords = gender === "female" ? femaleKeywords : maleKeywords;
    const genPool = pool.filter(v => keywords.some(k => v.name.toLowerCase().includes(k)));
    if (genPool.length) pool = genPool;
  }

  return pool[0] || voices.find(v => v.lang.startsWith("en")) || voices[0];
}

export function speak(word, voicePrefs) {
  window.speechSynthesis.cancel();
  const doSpeak = () => {
    const u = new SpeechSynthesisUtterance(word);
    const voice = findVoice(voicePrefs);
    if (voice) u.voice = voice;
    u.lang = voicePrefs?.accent || "en-AU";
    u.rate = 0.78;
    u.pitch = 1.05;
    u.volume = 1;
    window.speechSynthesis.speak(u);
  };
  if (getAvailableVoices().length > 0) { doSpeak(); }
  else { window.speechSynthesis.onvoiceschanged = () => { window.speechSynthesis.onvoiceschanged = null; doSpeak(); }; }
}

// ── File parsers ──────────────────────────────────────────
export function loadJSZip() {
  return new Promise((resolve, reject) => {
    if (window.JSZip) { resolve(window.JSZip); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    s.onload = () => resolve(window.JSZip);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function parseWordFile(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".txt") || name.endsWith(".csv")) {
    const text = await file.text();
    return text.split(/[\n,;\r]+/).map(w => w.trim().toLowerCase()).filter(w => w && /^[a-zA-Z\s'-]+$/.test(w));
  }
  if (name.endsWith(".docx")) {
    try {
      const JSZip = await loadJSZip();
      const zip = await JSZip.loadAsync(file);
      const xml = await zip.file("word/document.xml").async("string");
      const text = xml.replace(/<[^>]+>/g, " ").replace(/\s+/g, "\n");
      return text.split(/[\n,;\r]+/).map(w => w.trim().toLowerCase()).filter(w => w && /^[a-zA-Z\s'-]+$/.test(w));
    } catch {
      return [];
    }
  }
  return [];
}

export async function extractWordsFromImage(file, apiKey, model = "gemini-1.5-flash") {
  if (!apiKey) throw new Error("NO_KEY");

  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const mediaType = file.type || "image/png";
  const prompt = `This image contains a list of spelling words, possibly arranged in multiple columns or rows.
Extract every single word you can see. Return ONLY a JSON array of lowercase strings — nothing else, no explanation, no markdown, no numbering.
Example output: ["necessary","receive","separate","believe"]
If a word has a number prefix (like "1. necessary") strip the number. Include only real English words.`;

  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{
          parts: [
            { inline_data: { mime_type: mediaType, data: base64 } },
            { text: prompt }
          ]
        }]
      })
    }
  );

  if (!response.ok) throw new Error(`API error: ${response.status}`);
  const data = await response.json();
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || "";

  try {
    const clean = text.replace(/```json|```/g, "").trim();
    const words = JSON.parse(clean);
    if (Array.isArray(words)) return words.map(w => String(w).trim().toLowerCase()).filter(Boolean);
  } catch {}
  return [];
}

export async function lookupWord(word) {
  try {
    const r = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`);
    if (!r.ok) return null;
    const data = await r.json();
    if (!Array.isArray(data) || !data[0]) return null;
    const entry = data[0];
    const results = [];
    (entry.meanings || []).slice(0, 3).forEach(m => {
      const def = m.definitions?.[0];
      if (def) results.push({ partOfSpeech: m.partOfSpeech, definition: def.definition, example: def.example || null });
    });
    return { word: entry.word, phonetic: entry.phonetic || "", results };
  } catch { return null; }
}

export async function fetchSynonyms(word) {
  try {
    const res = await fetch(`https://api.datamuse.com/words?rel_syn=${encodeURIComponent(word)}&max=10`);
    const data = await res.json();
    return data.map(w => w.word);
  } catch { return []; }
}

export async function fetchAntonyms(word) {
  try {
    const res = await fetch(`https://api.datamuse.com/words?rel_ant=${encodeURIComponent(word)}&max=10`);
    const data = await res.json();
    return data.map(w => w.word);
  } catch { return []; }
}
